import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { getList, createEntity, updaateEntity, reorderCriteria } from '../../actions/criterias'
import Modal from 'react-modal'
import { useFormik } from 'formik'
import styles from './styles.module.scss'
import * as Yup from 'yup'
import { usePermission } from '../../hooks'
import HaventPermission from '../../assets/HaventPermission'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { MdClose } from 'react-icons/md'

const CATEGORY_META = {
  teacher_assessment: {
    label: 'Оценка преподавателя',
    usedFor: 'Опрос обучающихся',
    object: 'Преподаватель / преподаватель + дисциплина',
    description: 'Используется в старом сценарии: обучающийся оценивает преподавателей из расписания своей группы.',
    tone: 'orange',
    badgeClass: styles.badgeOrange,
  },
  educational_process: {
    label: 'Оценка образовательного процесса',
    usedFor: 'Опрос обучающихся',
    object: 'Образовательный процесс',
    description: 'Используется для оценки условий, содержания, организации и качества образовательного процесса.',
    tone: 'blue',
    badgeClass: styles.badgeBlue,
  },
  educational_organization: {
    label: 'Оценка организации образовательной деятельности',
    usedFor: 'Опрос преподавателей',
    object: 'Организация образовательной деятельности',
    description: 'Используется для оценки условий и организации образовательной деятельности преподавательским составом.',
    tone: 'green',
    badgeClass: styles.badgeGreen,
  },
}

const CATEGORY_KEYS = ['teacher_assessment', 'educational_process', 'educational_organization']

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Обязательное поле'),
  category: Yup.string().required('Обязательное поле'),
})

// Flexbox overlay: works even when a parent element has transform (common sidebar pattern)
// that breaks position:fixed + translate centering.
const MODAL_STYLE = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.5)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    boxSizing: 'border-box',
  },
  content: {
    // position: relative → participates in flex layout instead of being taken out of flow
    position: 'relative',
    top: 'auto',
    left: 'auto',
    right: 'auto',
    bottom: 'auto',
    padding: 0,
    border: 'none',
    borderRadius: '16px',
    boxShadow: '0 24px 70px rgba(15, 23, 42, 0.22)',
    overflow: 'hidden',
    width: '100%',
    maxWidth: '920px',
    maxHeight: 'calc(100vh - 40px)',
    outline: 'none',
  },
}

// ─── Sortable row ─────────────────────────────────────────────────────────────

const SortableRow = ({ item, canDrag, onOpen }) => {
  const isDragDisabled = !canDrag || item.is_archive
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    disabled: isDragDisabled,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  }
  const meta = CATEGORY_META[item.category] || {}

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`${styles.criterionRow} ${item.is_archive ? styles.isArchived : ''}`}
      onClick={() => onOpen(item)}
    >
      <td className={styles.dragCell}>
        <span
          className={`${styles.dragHandle} ${isDragDisabled ? styles.dragHandleDisabled : ''}`}
          {...(!isDragDisabled ? { ...attributes, ...listeners } : {})}
          onClick={e => e.stopPropagation()}
          title={
            item.is_archive
              ? 'Архивные критерии нельзя перемещать'
              : !canDrag
              ? 'Сортировка недоступна при активных фильтрах'
              : 'Перетащить внутри категории'
          }
        >
          ⋮⋮
        </span>
      </td>
      <td className={styles.orderCell}>{item.sort_order}</td>
      <td className={styles.criterionCell}>
        <div className={styles.criterionTitle}>{item.name}</div>
        <div className={styles.badges}>
          {item.is_required && <span className={`${styles.badge} ${styles.badgeDefault}`}>обязательный</span>}
          {item.is_used && <span className={`${styles.badge} ${styles.badgeOrange}`}>в опросах</span>}
          {meta.badgeClass && (
            <span className={`${styles.badge} ${meta.badgeClass}`}>{meta.label}</span>
          )}
        </div>
      </td>
      <td className={styles.usedForCell}>{meta.usedFor || '—'}</td>
      <td className={styles.statusCell}>
        {item.is_archive
          ? <span className={`${styles.badge} ${styles.badgeRed}`}>неактивен</span>
          : <span className={`${styles.badge} ${styles.badgeGreen}`}>активен</span>}
      </td>
    </tr>
  )
}

// ─── Main component ────────────────────────────────────────────────────────────

const Criterias = ({ list, loading, getList, createEntity, updaateEntity, reorderCriteria }) => {
  const { readRight, updateRight } = usePermission('Справочники', 'Критерии оценки качеств преподавателей для опроса обучающихся')

  const [filters, setFilters] = useState({ search: '', category: '', status: '' })
  const [localOrders, setLocalOrders] = useState(new Map())
  const [dirtyCategories, setDirtyCategories] = useState(new Set())
  const [modal, setModal] = useState(null)

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const canReorder = !filters.search && !filters.status

  const buildApiParams = (f = filters) => ({
    include_archived: f.status !== 'active',
    ...(f.category ? { category: f.category } : {}),
    ...(f.search ? { q: f.search } : {}),
  })

  useEffect(() => {
    getList(buildApiParams())
  }, [filters])

  const setFilter = (key, value) => {
    if (key !== 'category') {
      setDirtyCategories(new Set())
      setLocalOrders(new Map())
    }
    setFilters(prev => ({ ...prev, [key]: value }))
  }

  const getOrderedItems = (category, items) => {
    const catItems = items.filter(i => i.category === category)
    if (dirtyCategories.has(category) && localOrders.has(category)) {
      const orderedIds = localOrders.get(category)
      const sorted = orderedIds.map(id => catItems.find(i => i.id === id)).filter(Boolean)
      const remaining = catItems.filter(i => !orderedIds.includes(i.id))
      return [...sorted, ...remaining]
    }
    return [...catItems].sort((a, b) => (a.sort_order - b.sort_order) || (a.id - b.id))
  }

  const allList = list || []

  const displayList = filters.status === 'archived'
    ? allList.filter(i => i.is_archive)
    : allList

  const grouped = CATEGORY_KEYS
    .filter(key => !filters.category || key === filters.category)
    .map(key => ({
      key,
      meta: CATEGORY_META[key],
      items: getOrderedItems(key, displayList),
    }))
    .filter(g => g.items.length > 0)

  const foundCount = grouped.reduce((sum, g) => sum + g.items.length, 0)
  const studentCount = allList.filter(i => CATEGORY_META[i.category]?.usedFor === 'Опрос обучающихся').length
  const teacherCount = allList.filter(i => CATEGORY_META[i.category]?.usedFor === 'Опрос преподавателей').length
  const legacyCount = allList.filter(i => i.category === 'teacher_assessment').length

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return
    const activeItem = allList.find(i => i.id === active.id)
    const overItem = allList.find(i => i.id === over.id)
    if (!activeItem || !overItem || activeItem.category !== overItem.category) return
    const category = activeItem.category
    const catItems = getOrderedItems(category, allList.filter(i => i.category === category))
    const fromIdx = catItems.findIndex(i => i.id === active.id)
    const toIdx = catItems.findIndex(i => i.id === over.id)
    if (fromIdx === -1 || toIdx === -1) return
    const reordered = arrayMove(catItems, fromIdx, toIdx)
    setLocalOrders(prev => new Map([...prev, [category, reordered.map(i => i.id)]]))
    setDirtyCategories(prev => new Set([...prev, category]))
  }

  const handleSaveOrder = async () => {
    for (const category of dirtyCategories) {
      if (localOrders.has(category)) {
        const orderedIds = localOrders.get(category).filter(id => {
          const item = allList.find(i => i.id === id)
          return item && !item.is_archive
        })
        if (orderedIds.length > 0) await reorderCriteria(category, orderedIds)
      }
    }
    setDirtyCategories(new Set())
    setLocalOrders(new Map())
    getList(buildApiParams())
  }

  const handleSaved = () => {
    setModal(null)
    setDirtyCategories(new Set())
    setLocalOrders(new Map())
    getList(buildApiParams())
  }

  if (!readRight) return <HaventPermission />

  return (
    <div className={styles.criteriasWrap}>

      {/* ── Hero: title + stats + action ── */}
      <div className={styles.heroBlock}>
        <div className={styles.heroLeft}>
          <h1 className={styles.heroTitle}>Критерии оценки качества</h1>
          <p className={styles.heroSubtitle}>
            {/* Банк критериев для последующего формирования опросов. Порядок задаётся внутри категории; респондент определяется в конкретном опросе. */}
          </p>
          <div className={styles.heroStats}>
            <div className={styles.heroStat}>
              <span className={styles.heroStatNum}>{allList.length}</span>
              <span className={styles.heroStatLabel}>всего критериев</span>
            </div>
            <div className={styles.heroStat}>
              <span className={styles.heroStatNum}>{studentCount}</span>
              <span className={styles.heroStatLabel}>для обучающихся</span>
            </div>
            <div className={styles.heroStat}>
              <span className={styles.heroStatNum}>{teacherCount}</span>
              <span className={styles.heroStatLabel}>для преподавателей</span>
            </div>
          </div>
        </div>
        {updateRight && (
          <div className={styles.heroRight}>
            <button className={styles.addButton} onClick={() => setModal({})}>+ Добавить критерий</button>
          </div>
        )}
      </div>

      {/* ── Main card ── */}
      <div className={styles.card}>
        <div className={styles.cardTop}>
          <div>
            <div className={styles.cardTitle}>Список критериев</div>
            <div className={styles.cardSubtitle}>
              {/* Критерии сгруппированы по категориям. Перетаскивание изменяет порядок только внутри категории. */}
              </div>
          </div>
          <div className={styles.foundRow}>
            <span className={styles.foundLabel}>Найдено: <b>{foundCount}</b></span>
            {dirtyCategories.size > 0 && (
              <button className={styles.footBtnSave} onClick={handleSaveOrder}>Сохранить порядок</button>
            )}
          </div>
        </div>

        <div className={styles.filters}>
          <input
            type="text"
            placeholder="Поиск по тексту критерия"
            value={filters.search}
            onChange={e => setFilter('search', e.target.value)}
            className={styles.searchInput}
          />
          <select value={filters.category} onChange={e => setFilter('category', e.target.value)} className={styles.filterSelect}>
            <option value="">Все категории</option>
            {CATEGORY_KEYS.map(k => <option key={k} value={k}>{CATEGORY_META[k].label}</option>)}
          </select>
          <select value={filters.status} onChange={e => setFilter('status', e.target.value)} className={styles.filterSelect}>
            <option value="">Все статусы</option>
            <option value="active">Активные</option>
            <option value="archived">Архивные</option>
          </select>
        </div>

        {!canReorder && (
          <div className={styles.hintOrange}>
            Для изменения порядка очистите поиск и сбросьте фильтр «Статус». Сортировка сохраняется внутри категории.
          </div>
        )}

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.thDrag}></th>
                  <th className={styles.thOrder}>№</th>
                  <th>Критерий</th>
                  <th>Используется для</th>
                  <th>Статус</th>
                </tr>
              </thead>
              <tbody>
                {grouped.length === 0 ? (
                  <tr>
                    <td colSpan={5} className={styles.emptyCell}>
                      {loading ? 'Загрузка...' : 'Критерии не найдены'}
                    </td>
                  </tr>
                ) : (
                  grouped.map(({ key, meta, items }) => (
                    <React.Fragment key={key}>
                      <tr className={styles.groupRow}>
                        <td colSpan={5}>
                          <div className={styles.groupLine}>
                            <div className={styles.groupLeft}>
                              <span className={`${styles.badge} ${meta.badgeClass}`}>{meta.label}</span>
                              <span className={styles.muted}>{items.length} крит.</span>
                              {dirtyCategories.has(key) && (
                                <span className={`${styles.badge} ${styles.badgeBlue}`}>порядок изменен</span>
                              )}
                            </div>
                            <span className={styles.muted}>{meta.usedFor}</span>
                          </div>
                        </td>
                      </tr>
                      <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
                        {items.map(item => (
                          <SortableRow
                            key={item.id}
                            item={item}
                            canDrag={canReorder}
                            onOpen={updateRight ? setModal : () => {}}
                          />
                        ))}
                      </SortableContext>
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </DndContext>

        
      </div>

      {modal !== null && (
        <AddEditModal
          isOpen
          onRequestClose={() => setModal(null)}
          data={modal}
          addMode={!modal.id}
          createEntity={createEntity}
          updaateEntity={updaateEntity}
          updateRight={updateRight}
          onSaved={handleSaved}
        />
      )}
    </div>
  )
}

// ─── Add / Edit modal ──────────────────────────────────────────────────────────

const AddEditModal = ({ isOpen, onRequestClose, data, addMode, createEntity, updaateEntity, updateRight, onSaved }) => {
  const isUsed = data?.is_used || false

  const { values, errors, touched, submitCount, handleChange, handleReset, handleSubmit, setFieldValue } = useFormik({
    initialValues: addMode
      ? { name: '', category: 'educational_process', is_required: true, is_archive: false, hint: '', note: '' }
      : {
          id: data?.id,
          name: data?.name || '',
          category: data?.category || 'educational_process',
          is_required: data?.is_required ?? true,
          is_archive: data?.is_archive ?? false,
          hint: data?.hint || '',
          note: data?.note || '',
        },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async vals => {
      const payload = {
        name: vals.name,
        category: vals.category,
        is_required: vals.is_required,
        is_archive: vals.is_archive,
        hint: vals.hint || null,
        note: vals.note || null,
      }
      let success
      if (addMode) {
        success = await createEntity(payload)
      } else {
        success = await updaateEntity(vals.id, payload)
      }
      if (success) onSaved()
    },
  })

  useEffect(() => {
    if (!isOpen) return
    handleReset()
  }, [isOpen])

  const meta = CATEGORY_META[values.category] || {}
  const fieldError = field => (touched[field] || submitCount > 0) && errors[field]
  const canEditMeta = updateRight && !isUsed

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel="Критерий оценки"
      overlayClassName={styles.critModalOverlay}
      className={styles.critModalContent}
    >
      {/* ── Modal shell: flex-column so header/footer stay fixed, body scrolls ── */}
      <div className={styles.modalShell}>

        {/* Header */}
        <div className={styles.modalHead}>
          <div className={styles.modalTitleRow}>
            <div>
              <h3 className={styles.modalTitle}>
                {addMode ? 'Добавление критерия оценки' : 'Просмотр / редактирование критерия'}
              </h3>
              <div className={styles.modalSubtitle}>
                {/* Критерий хранится в справочнике. Сортировка задаётся внутри выбранной категории. */}
              </div>
            </div>
          </div>
          <button className={styles.closeBtn} onClick={onRequestClose} title="Закрыть"><MdClose /></button>
        </div>

        {/* Body: left form + right info panel */}
        <div className={styles.modalBody}>

          {/* ── Left: form ── */}
          <div className={styles.formSide}>

            {isUsed && (
              <div className={styles.warningBox}>
                Критерий уже использовался в опросе. Изменение названия и категории невозможно, чтобы не исказить результаты ранее проведённых опросов.
              </div>
            )}

            <div className={styles.formGroup}>
              <span className={styles.formLabel}>Текст критерия *</span>
              <textarea
                name="name"
                value={values.name}
                onChange={handleChange}
                disabled={!canEditMeta}
                placeholder="Например: Удовлетворённость условиями обучения и материально-техническим обеспечением учебного процесса"
                className={styles.textarea}
                rows={3}
              />
              {fieldError('name') && <span className={styles.fieldError}>{fieldError('name')}</span>}
            </div>

            <div className={styles.grid2}>
              <div className={styles.formGroup}>
                <span className={styles.formLabel}>Категория критерия *</span>
                <select
                  name="category"
                  value={values.category}
                  onChange={handleChange}
                  disabled={!canEditMeta}
                  className={styles.select}
                >
                  {CATEGORY_KEYS.map(k => (
                    <option key={k} value={k}>{CATEGORY_META[k].label}</option>
                  ))}
                </select>
              </div>
              <div className={styles.formGroup}>
                <span className={styles.formLabel}>Порядок в категории</span>
                <input
                  type="text"
                  readOnly
                  value={addMode ? 'Будет назначен автоматически' : (data?.sort_order ?? '—')}
                  className={`${styles.input} ${styles.inputReadonly}`}
                />
                <div className={styles.fieldHint}>Номер используется только внутри выбранной категории.</div>
              </div>
            </div>

            <div className={styles.readonlyGrid}>
              <div className={styles.readonlyField}>
                <div className={styles.readonlyLabel}>Используется для</div>
                <div className={styles.readonlyValue}>{meta.usedFor || '—'}</div>
                <div className={styles.readonlyDesc}>Определяется автоматически по категории критерия.</div>
              </div>
              <div className={styles.readonlyField}>
                <div className={styles.readonlyLabel}>Объект оценки</div>
                <div className={styles.readonlyValue}>{meta.object || '—'}</div>
                <div className={styles.readonlyDesc}>Фактический объект уточняется при настройке опроса.</div>
              </div>
            </div>

            <div className={styles.toggleGrid}>
              <div
                className={`${styles.toggleItem} ${!updateRight ? styles.toggleDisabled : ''}`}
                onClick={() => updateRight && setFieldValue('is_required', !values.is_required)}
              >
                <div>
                  <div className={styles.toggleTitle}>Обязательный ответ</div>
                  <div className={styles.toggleDesc}>Пользователь не сможет отправить опрос без ответа.</div>
                </div>
                <div className={`${styles.switchEl} ${values.is_required ? '' : styles.switchOff}`} />
              </div>
              <div
                className={`${styles.toggleItem} ${!updateRight ? styles.toggleDisabled : ''}`}
                onClick={() => updateRight && setFieldValue('is_archive', !values.is_archive)}
              >
                <div>
                  <div className={styles.toggleTitle}>Активный критерий</div>
                  <div className={styles.toggleDesc}>Неактивные критерии не предлагаются для новых опросов.</div>
                </div>
                <div className={`${styles.switchEl} ${!values.is_archive ? '' : styles.switchOff}`} />
              </div>
            </div>

            <div className={styles.formGroup}>
              <span className={styles.formLabel}>Подсказка для пользователя</span>
              <input
                type="text"
                name="hint"
                value={values.hint}
                onChange={handleChange}
                disabled={!updateRight}
                placeholder="Необязательное пояснение к критерию"
                className={styles.input}
              />
            </div>

            <div className={styles.formGroup}>
              <span className={styles.formLabel}>Служебное примечание</span>
              <textarea
                name="note"
                value={values.note}
                onChange={handleChange}
                disabled={!updateRight}
                placeholder="Комментарий для администратора, не отображается участникам опроса"
                className={styles.textarea}
                rows={3}
              />
            </div>

          </div>{/* /formSide */}

          {/* ── Right: info panel ── */}
          <aside className={styles.infoSide}>
            <div className={styles.scaleCard}>
              <div className={styles.scaleTitle}>Используемая шкала</div>
              {[
                '5 — полностью удовлетворён',
                '4 — скорее удовлетворён',
                '3 — частично удовлетворён',
                '2 — скорее не удовлетворён',
                '1 — не удовлетворён',
              ].map(s => (
                <div key={s} className={styles.scaleItem}>{s}</div>
              ))}
            </div>

            <div className={`${styles.sideNote} ${styles.sideNoteBlue}`}>
              <b>Сортировка</b><br />
              Порядок критерия действует только внутри категории. В общем списке критерии группируются по категориям и сортируются по этому номеру.
            </div>

          </aside>

        </div>{/* /modalBody */}

        {/* Footer */}
        <div className={styles.modalFoot}>
          <button className={styles.footBtnCancel} onClick={onRequestClose}>Отмена</button>
          {updateRight && (
            <button className={styles.footBtnSave} onClick={handleSubmit}>Сохранить критерий</button>
          )}
        </div>

      </div>{/* /modalShell */}
    </Modal>
  )
}


const mapStateToProps = ({ criterias: { list, loading } }) => ({ list, loading })

export default connect(mapStateToProps, { getList, createEntity, updaateEntity, reorderCriteria })(Criterias)
